import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PasswordInput from '@/components/PasswordInput';
import { FormCard, FormCardMessage } from '@/components/ui/FormCard';

import { apiClient } from '@/services/api';

const ResetPasswordForm: React.FC = () => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [message, setMessage] = useState<FormCardMessage | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      setIsValidatingToken(true);
      try {
        const response = await apiClient.validateResetToken(token);
        if (!response.data.success) {
          setTokenError(true);
          setMessage({ type: 'error', text: t('resetLinkInvalid') });
        }
      } catch (err: any) {
        console.error('Token validation error:', err);
        const errorResponse = err.response?.data;
        setTokenError(true);
        if (errorResponse?.error === 'token_expired') {
          setMessage({ type: 'error', text: t('resetLinkExpired') });
        } else if (errorResponse?.error === 'token_missing') {
          setMessage({ type: 'error', text: t('missingResetToken') });
        } else {
          setMessage({ type: 'error', text: t('resetLinkInvalid') });
        }
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateToken();
  }, [token, navigate, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!token) {
      setMessage({ type: 'error', text: t('missingResetToken') });
      return;
    }

    if (!newPassword) {
      setMessage({ type: 'error', text: t('enterNewPassword') });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: t('passwordMinLength') });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: t('passwordsDoNotMatch') });
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.resetPassword(token, newPassword);

      if (response.data.success) {
        setSuccess(true);
        setNewPassword('');
        setConfirmPassword('');
        setMessage({ type: 'success', text: t('passwordChangedSuccessfully') });

        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      } else {
        setMessage({ type: 'error', text: response.data.message || t('resetPasswordError') });
      }
    } catch (err: any) {
      console.error('Reset password error:', err);
      const errorResponse = err.response?.data;

      if (errorResponse?.error === 'token_expired') {
        setTokenError(true);
        setMessage({ type: 'error', text: t('resetLinkExpired') });
      } else {
        setMessage({ type: 'error', text: errorResponse?.message || t('resetPasswordErrorRetry') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = () => {
    if (newPassword.length < 6) return { width: '33%', color: 'var(--red)', label: t('passwordStrengthWeak') };
    if (newPassword.length < 10) return { width: '66%', color: 'var(--accent)', label: t('passwordStrengthMedium') };
    return { width: '100%', color: 'var(--green)', label: t('passwordStrengthStrong') };
  };

  if (isValidatingToken) {
    return (
      <FormCard title={t('resetPasswordAction')}>
        <p className="text-12" style={{ margin: '10px 0', textAlign: 'center' }}>
          {t('verifyingLink')}
        </p>
      </FormCard>
    );
  }

  if (tokenError) {
    return (
      <FormCard title={t('resetPasswordAction')} message={message}>
        <div className="d-flex flex-column g-16" style={{ marginTop: '10px' }}>
          <Link to="/forgot-password" className="btn" style={{ textAlign: 'center' }}>
            {t('requestNewLink')}
          </Link>
        </div>
        <div className="d-flex justify-content-center align-items-center g-10" style={{ marginTop: '20px' }}>
          <Link to="/login" className="text-button text-button--sm">
            {t('backToLogin')}
          </Link>
        </div>
      </FormCard>
    );
  }

  if (success) {
    return (
      <FormCard title={t('resetPasswordAction')} message={message}>
        <div className="d-flex justify-content-center align-items-center g-10" style={{ marginTop: '20px' }}>
          <Link to="/login" className="text-button text-button--sm">
            {t('goToLogin')}
          </Link>
        </div>
      </FormCard>
    );
  }

  return (
    <FormCard title={t('newPassword')} subtitle={t('newPasswordSubtitle')} message={message}>
      <form onSubmit={handleSubmit}>
        <div className="d-flex flex-column g-20" style={{ margin: '20px 0 30px' }}>
          <PasswordInput
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t('minimum6Chars')}
            disabled={isLoading}
            autoFocus
          />

          {newPassword && (
            <div className="d-flex align-items-center g-10">
              <div style={{ flex: 1, height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: getPasswordStrength().width,
                    background: getPasswordStrength().color,
                    transition: 'width 0.3s',
                  }}
                />
              </div>
              <span className="text-12">{getPasswordStrength().label}</span>
            </div>
          )}

          <PasswordInput
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('confirmPasswordPlaceholder')}
            disabled={isLoading}
          />
        </div>

        <button
          className="btn"
          type="submit"
          disabled={isLoading || !newPassword || !confirmPassword}
        >
          {isLoading ? t('resettingPassword') : t('resetPasswordAction')}
        </button>
      </form>

      <div className="d-flex justify-content-center align-items-center g-10" style={{ marginTop: '20px' }}>
        <Link to="/login" className="text-button text-button--sm">
          {t('backToLogin')}
        </Link>
      </div>
    </FormCard>
  );
};

export default ResetPasswordForm;
